import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'components/ui/toast-store'
import { Search } from 'lucide-react'
import { initializeActiveLocations } from 'slices/locationSlice'
import { addLocationToPortfolio } from 'slices/portfolioSlice'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Button } from 'components/ui/button'
import { Spinner } from 'components/ui/spinner'
import AltRetailerLogo from 'images/Old_Navy_Logo.png'

const PortfolioAddExistingLocationPopup = () => {
	const { id: portfolioId } = useParams()
	const dispatch = useDispatch()

	const [searchValue, setSearchValue] = useState('')

	const { locations, isLoading } = useSelector(state => {
		const locationIds = state.locations.ids
		const locations = locationIds.map(id => state.locations.entities[id])
		return { locations, isLoading: !state.locations.isInitialized }
	})

	const { portfolio } = useSelector(state => ({
		portfolio: state.portfolios.entities[portfolioId] || {},
	}))

	useEffect(() => {
		dispatch(initializeActiveLocations())
	}, [dispatch])

	const portfolioLocationIds = (portfolio.locations || []).map(item => item.location._id)

	const filtered = locations
		.filter(loc => !portfolioLocationIds.includes(loc._id))
		.filter(loc => {
			if (!searchValue) return true
			const lower = searchValue.toLowerCase()
			return (
				(loc.assetName || '').toLowerCase().includes(lower) ||
				(loc.address || '').toLowerCase().includes(lower) ||
				(loc.city || '').toLowerCase().includes(lower) ||
				(loc.stateProvince || '').toLowerCase().includes(lower) ||
				(loc.postalCode || '').toLowerCase().includes(lower)
			)
		})

	filtered.sort((a, b) => (a.assetName || '').localeCompare(b.assetName || ''))

	const addLocation = async locationId => {
		if (!portfolioId) {
			toast.warning('You must enter a Portfolio Name')
		} else {
			await dispatch(addLocationToPortfolio({ portfolioId, locationId }))
		}
	}

	return (
		<div className="w-full">
			{/* Search */}
			<div className="relative mb-4">
				<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
				<input
					type="text"
					placeholder="Search by name, address, city, state or postal code..."
					value={searchValue}
					onChange={e => setSearchValue(e.target.value)}
					className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#0080fe] focus:outline-none focus:ring-1 focus:ring-[#0080fe]"
				/>
			</div>

			{/* Locations list */}
			{isLoading ? (
				<div className="py-8">
					<Spinner />
				</div>
			) : filtered.length === 0 ? (
				<div className="py-12 text-center text-sm text-gray-400">
					{searchValue ? 'No locations match your search.' : 'No available locations to add.'}
				</div>
			) : (
				<div className="divide-y divide-gray-100">
					{filtered.map(location => (
						<div key={location._id} className="flex items-center gap-3 py-3">
							<Avatar className="h-9 w-9 flex-shrink-0">
								<AvatarImage src={location.image} alt={location.assetName || ''} />
								<AvatarFallback>
									<img src={AltRetailerLogo} className="h-full w-full object-cover" alt="" />
								</AvatarFallback>
							</Avatar>
							<span className="text-sm font-medium text-gray-900 min-w-0 truncate">
								{location.assetName}
							</span>
							<span className="text-sm text-gray-500 min-w-0 truncate">
								{[location.city, location.stateProvince].filter(Boolean).join(', ')}
							</span>
							<span className="text-sm text-gray-400 min-w-0 truncate">
								{location.postalCode}
							</span>
							<div className="ml-auto flex-shrink-0">
								<Button variant="primary" isOutline className="text-xs" onClick={() => addLocation(location._id)}>
									Add Location
								</Button>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	)
}

export default PortfolioAddExistingLocationPopup
